'use client';

import { memo } from 'react';
import Link from 'next/link';
import { formatDistanceToNow } from 'date-fns';
import {
  Activity,
  AlertTriangle,
  ChevronDown,
  ExternalLink,
  Mail,
  Maximize2,
  MessageSquare,
  Sparkles,
  User,
  Video,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { AccountInsightsUpdatedAt } from '@/components/accounts/account-insights-updated-at';
import type { OutreachContextPreview } from '@/lib/outreach/types';
import type { RecipientTouchpoint } from '@/lib/outreach/recipient/types';
import { cn } from '@/lib/utils';

type OutreachContextPanelProps = {
  preview: OutreachContextPreview | null;
  loading?: boolean;
  recipientName?: string;
  accountId?: string | null;
  dealId?: string | null;
};

const healthStyles: Record<string, { label: string; className: string }> = {
  healthy: { label: 'Healthy', className: 'bg-emerald-500/20 text-emerald-100 border-emerald-400/30' },
  moderate: { label: 'Moderate', className: 'bg-amber-500/20 text-amber-100 border-amber-400/30' },
  at_risk: { label: 'At risk', className: 'bg-red-500/20 text-red-100 border-red-400/30' },
};

const touchpointIcons: Record<RecipientTouchpoint['channel'], typeof Mail> = {
  private_chat: MessageSquare,
  room_chat: MessageSquare,
  email: Mail,
  meeting: Video,
};

const touchpointLabels: Record<RecipientTouchpoint['channel'], string> = {
  private_chat: 'Private chat',
  room_chat: 'Room chat',
  email: 'Email',
  meeting: 'Meeting',
};

function formatRelative(iso: string | null): string {
  if (!iso) return 'never';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return 'unknown';
  }
}

export const OutreachContextPanel = memo(function OutreachContextPanel({
  preview,
  loading,
  recipientName,
  accountId,
  dealId,
}: OutreachContextPanelProps) {
  if (loading) {
    return (
      <Card className="border shadow-sm overflow-hidden animate-pulse">
        <CardHeader className="pb-2">
          <div className="h-4 w-40 bg-muted rounded" />
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="h-3 w-full bg-muted rounded" />
          <div className="h-3 w-3/4 bg-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  if (!preview) {
    return (
      <p className="text-sm text-muted-foreground px-1">
        Select a room member to see their profile, activity, and account context for your draft.
      </p>
    );
  }

  const { recipient, accountInsights, deal } = preview;
  const hasInsights = accountInsights.available;
  const health = accountInsights.healthAssessment
    ? healthStyles[accountInsights.healthAssessment]
    : null;

  const hasRecipientContext =
    Boolean(recipientName) ||
    recipient.stakeholderRoleLabel ||
    recipient.roomRole ||
    recipient.totalActivity30d > 0 ||
    recipient.touchpoints.length > 0 ||
    recipient.recentContentViews.length > 0;

  return (
    <div className="space-y-3">
      {hasRecipientContext && (
        <Card className="border shadow-sm overflow-hidden">
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0 space-y-1">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="h-4 w-4 text-primary shrink-0" />
                  <span className="truncate">{recipientName ?? 'Recipient'}</span>
                </CardTitle>
                <div className="flex flex-wrap gap-1.5">
                  {recipient.stakeholderRoleLabel && (
                    <Badge variant="secondary" className="text-xs font-normal">
                      {recipient.stakeholderRoleLabel}
                    </Badge>
                  )}
                  {recipient.inferredEmailRoleLabel &&
                    recipient.inferredEmailRoleLabel !== recipient.stakeholderRoleLabel && (
                      <Badge variant="outline" className="text-xs font-normal">
                        Email: {recipient.inferredEmailRoleLabel}
                      </Badge>
                    )}
                  {recipient.roomRole && (
                    <Badge variant="outline" className="text-xs font-normal">
                      Room {recipient.roomRole.toLowerCase()}
                    </Badge>
                  )}
                </div>
              </div>
              {accountId && recipient.stakeholderRoleLabel === null && (
                <Button variant="ghost" size="sm" className="h-7 text-xs shrink-0" asChild>
                  <Link href={`/accounts/${accountId}?tab=relationship-map`}>
                    Set stakeholder role
                  </Link>
                </Button>
              )}
            </div>
          </CardHeader>

          <CardContent className="px-4 pb-4 space-y-3">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1">
                <Activity className="h-3.5 w-3.5" />
                Active {formatRelative(recipient.lastActiveAt)}
              </span>
              <span>
                {recipient.visits30d} visit{recipient.visits30d === 1 ? '' : 's'} ·{' '}
                {recipient.contentViews30d} content view
                {recipient.contentViews30d === 1 ? '' : 's'} (30d)
              </span>
              {recipient.invitesSentCount > 0 && (
                <span>
                  Invited {recipient.invitesSentCount} member
                  {recipient.invitesSentCount === 1 ? '' : 's'}
                </span>
              )}
              {recipient.invitedByName && (
                <span>Invited by {recipient.invitedByName}</span>
              )}
              {recipient.emailThreadsParticipated > 0 && (
                <span>
                  {recipient.emailThreadsParticipated} email thread
                  {recipient.emailThreadsParticipated === 1 ? '' : 's'}
                </span>
              )}
            </div>

            {recipient.recentContentViews.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-foreground">Viewed in room</p>
                <ul className="space-y-1">
                  {recipient.recentContentViews.map((view) => (
                    <li
                      key={`${view.contentId ?? view.contentName}-${view.viewedAt}`}
                      className="text-xs text-muted-foreground line-clamp-1"
                    >
                      {view.contentName}
                      <span className="text-muted-foreground/70">
                        {' '}
                        · {formatRelative(view.viewedAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {recipient.touchpoints.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-foreground">Recent touchpoints</p>
                <ul className="space-y-2">
                  {recipient.touchpoints.map((point) => {
                    const Icon = touchpointIcons[point.channel];
                    return (
                      <li
                        key={`${point.channel}-${point.at}-${point.title ?? point.summary.slice(0, 24)}`}
                        className="text-xs"
                      >
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Icon className="h-3.5 w-3.5 shrink-0" />
                          <span className="font-medium text-foreground/80">
                            {touchpointLabels[point.channel]}
                          </span>
                          {point.title && (
                            <span className="truncate text-foreground">{point.title}</span>
                          )}
                          <span className="shrink-0">· {formatRelative(point.at)}</span>
                        </div>
                        <p className="text-muted-foreground line-clamp-2 pl-5 mt-0.5">
                          {point.summary}
                        </p>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {hasInsights && (
        <Collapsible defaultOpen={false}>
          <Card className="border-none shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2 min-h-8">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex items-center gap-1.5 min-w-0 flex-1 text-left group"
                  >
                    <div className="flex items-center justify-center bg-white/20 p-1 rounded-md shrink-0">
                      <Sparkles className="h-3.5 w-3.5 text-white" />
                    </div>
                    <span className="text-sm font-medium text-white shrink-0">
                      Account backdrop
                    </span>
                    <ChevronDown className="h-4 w-4 text-white/70 shrink-0 transition-transform group-data-[state=open]:rotate-180" />
                    {accountInsights.generatedAt && (
                      <>
                        <span className="text-white/35 text-xs hidden sm:inline" aria-hidden>
                          ·
                        </span>
                        <AccountInsightsUpdatedAt
                          generatedAt={accountInsights.generatedAt}
                          className="text-white/60 text-[11px] truncate min-w-0 hidden sm:inline"
                        />
                      </>
                    )}
                    {health && (
                      <Badge
                        variant="outline"
                        className={cn(
                          'border text-[10px] px-1.5 py-0 h-5 shrink-0 hidden md:inline-flex',
                          health.className
                        )}
                      >
                        {health.label}
                      </Badge>
                    )}
                    {accountInsights.isStale && (
                      <Badge
                        variant="outline"
                        className="border-amber-400/40 text-amber-100 text-[10px] px-1.5 py-0 h-5 shrink-0 hidden md:inline-flex"
                      >
                        <AlertTriangle className="h-2.5 w-2.5 mr-0.5" />
                        Stale
                      </Badge>
                    )}
                  </button>
                </CollapsibleTrigger>
                {accountId && (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-7 px-2.5 text-xs bg-white/20 text-white hover:bg-white/30 border-0 shrink-0"
                    asChild
                  >
                    <Link href={`/accounts/${accountId}?tab=insights`}>
                      <Maximize2 className="h-3.5 w-3.5 mr-1" />
                      Full report
                    </Link>
                  </Button>
                )}
              </div>

              <CollapsibleContent>
                <div className="rounded-lg bg-white/10 backdrop-blur-sm px-2.5 py-2 space-y-1">
                  {accountInsights.headline && (
                    <p className="text-sm font-medium text-white leading-snug line-clamp-2">
                      {accountInsights.headline}
                    </p>
                  )}
                  {accountInsights.engagementSummary && (
                    <p className="text-xs text-white/75 line-clamp-3 leading-relaxed">
                      {accountInsights.engagementSummary}
                    </p>
                  )}
                </div>
              </CollapsibleContent>
            </div>
          </Card>
        </Collapsible>
      )}

      {!hasInsights && accountId && (
        <Card className="border-dashed">
          <div className="p-3 flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="text-muted-foreground">No account insights yet.</span>
            <Button variant="outline" size="sm" asChild>
              <Link href={`/accounts/${accountId}?tab=insights`}>
                <Sparkles className="h-3.5 w-3.5 mr-1" />
                Generate insights
              </Link>
            </Button>
          </div>
        </Card>
      )}

      {deal.available && (deal.topSignals[0] || deal.dealName) && (
        <div className="flex flex-wrap items-start justify-between gap-2 rounded-lg border px-3 py-2 text-sm">
          <div className="min-w-0">
            <p className="font-medium text-foreground">
              Deal{deal.dealName ? `: ${deal.dealName}` : ''}
            </p>
            {deal.topSignals[0] && (
              <p className="text-muted-foreground text-xs line-clamp-2 mt-0.5">
                {deal.topSignals[0]}
              </p>
            )}
          </div>
          {dealId && (
            <Button variant="ghost" size="sm" className="h-7 shrink-0" asChild>
              <Link href={`/deals/${dealId}?tab=insights`}>
                <ExternalLink className="h-3.5 w-3.5 mr-1" />
                View deal
              </Link>
            </Button>
          )}
        </div>
      )}
    </div>
  );
});
